/**
 * GFM pipe-table serialization for the notebook editor.
 *
 * `tiptap-markdown` ships a table serializer internally, but it is not
 * exported and it falls back to raw HTML for every shape GFM cannot
 * express (merged cells, cells with more than one paragraph). Raw HTML is
 * not an option here: task descriptions and comments are rendered with
 * `react-markdown` (no raw-HTML pass), so HTML would show up as literal
 * text in the read path.
 *
 * This module owns the table serialization instead:
 *   - The first row is always the header row (`| a | b |` + `| --- | --- |`).
 *   - A cell holds a single paragraph of inline content, so bold / italic /
 *     code / links survive the round trip.
 *   - Literal pipes are escaped as `\|` and backslashes as `\\` (the
 *     markdown serializer escapes the backslash for us), so a cell can
 *     never break out of its column.
 *   - Nothing is ever written as HTML.
 *
 * The module deliberately has **no runtime imports** so the task editor
 * can reuse `GFM_TABLE_SKELETON` to drop a table into a description or a
 * comment without pulling the editor into that bundle.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import type { MarkdownNodeSpec } from 'tiptap-markdown'

/** Rows (header row included) and columns of the tables we insert. */
export const TABLE_ROWS = 3
export const TABLE_COLS = 3

/**
 * A ready-to-paste GFM table: one header row, the delimiter row and two
 * body rows. Used by the task description / comment editor, which stays a
 * plain markdown textarea.
 */
export const GFM_TABLE_SKELETON = [
  '|  |  |  |',
  '| --- | --- | --- |',
  '|  |  |  |',
  '|  |  |  |',
].join('\n')

/**
 * The serializer state as tiptap-markdown actually implements it. The
 * published typings hide the output buffer (`out`) and the "a block was
 * just closed" flag (`closed`), but the table serializer needs both: a
 * cell is rendered into an isolated buffer so that its text can be
 * pipe-escaped without leaking into the rest of the document.
 */
type MarkdownSerializerState = Parameters<MarkdownNodeSpec['serialize']>[0]

interface TableSerializerState extends MarkdownSerializerState {
  out: string
  closed: ProseMirrorNode | null
}

/** Renders one cell to its inline markdown (without the surrounding pipes). */
export type GfmCellRenderer = (cell: ProseMirrorNode) => string

/** Escapes the pipes that would otherwise split a cell into columns. */
export function escapeGfmCellPipes(text: string): string {
  return text.replace(/\|/g, '\\|')
}

/**
 * Renders a table node as the lines of a GFM pipe table. The first row is
 * the header row, followed by the `| --- |` delimiter row.
 */
export function renderGfmTable(table: ProseMirrorNode, renderCell: GfmCellRenderer): string[] {
  const rows: ProseMirrorNode[] = []
  table.forEach((row) => rows.push(row))
  if (rows.length === 0) return []

  const columns = rows.reduce((max, row) => Math.max(max, row.childCount), 0)
  const lines: string[] = []
  rows.forEach((row, index) => {
    lines.push(renderGfmRow(row, columns, renderCell))
    if (index === 0) lines.push(renderGfmDelimiterRow(columns))
  })
  return lines
}

function renderGfmRow(row: ProseMirrorNode, columns: number, renderCell: GfmCellRenderer): string {
  const cells: string[] = []
  for (let index = 0; index < columns; index += 1) {
    const cell = index < row.childCount ? row.child(index) : null
    const text = cell ? escapeGfmCellPipes(renderCell(cell).trim()) : ''
    cells.push(text ? ` ${text} ` : ' ')
  }
  return `|${cells.join('|')}|`
}

function renderGfmDelimiterRow(columns: number): string {
  return `|${new Array(columns).fill(' --- ').join('|')}|`
}

/**
 * The markdown hook for the `table` node. Registered through the table
 * extension's `addStorage()`; `tiptap-markdown` picks it up by node name
 * and prefers it over its own (internal, HTML-fallbacking) serializer.
 */
export const tableMarkdownSpec: MarkdownNodeSpec = {
  serialize(state, node) {
    serializeTableToMarkdown(state, node)
  },
}

/** Writes a table node to the markdown output as a GFM pipe table. */
export function serializeTableToMarkdown(state: MarkdownSerializerState, table: ProseMirrorNode): void {
  const lines = renderGfmTable(table, (cell) => renderCellInline(state, cell))
  for (const line of lines) {
    state.write(line)
    state.ensureNewLine()
  }
  state.closeBlock(table)
}

/**
 * Renders the inline content of a cell. The cell's paragraph is rendered
 * into a throw-away buffer so the result can be escaped (pipes) before it
 * is written into the table row.
 */
function renderCellInline(state: MarkdownSerializerState, cell: ProseMirrorNode): string {
  const paragraph = cell.firstChild
  if (!paragraph) return ''

  const buffer = state as TableSerializerState
  const previousOut = buffer.out
  const previousClosed = buffer.closed
  buffer.out = ''
  buffer.closed = null
  try {
    buffer.renderInline(paragraph)
    // A hard break ends the table row — GFM cells are single-line — so it
    // collapses into a space instead of breaking the table apart.
    return buffer.out.replace(/\\\n/g, ' ').trim()
  } finally {
    buffer.out = previousOut
    buffer.closed = previousClosed
  }
}
